import { paintBanner } from '../game/banners/BannerCloth';
import {
  createBanner,
  type Banner,
  type BannerDesign,
  type BannerSpec,
} from '../network/Banners';

/**
 * Edytor sztandaru: płótno z pól, paleta i kilka gotowych podziałów.
 *
 * **Raz i na zawsze.** Zapis pyta o potwierdzenie, bo serwer drugiego nie
 * przyjmie — edytor nie ma czego „zapisać zmian", ma co **wznieść**.
 *
 * **Dwie barwy pod dwoma przyciskami myszy**: lewy maluje pierwszą, prawy
 * drugą, a podziały pola kładą obie. Tak rysuje się heraldykę — pole i godło —
 * bez ciągłego przełączania koloru.
 *
 * Pola spoza kształtu są przyciemnione i nie dają się malować; serwer i tak by
 * je wyzerował.
 */

type Tool = 'pencil' | 'fill';

/** Ile pikseli ekranu na jedno pole projektu. */
const CELL = 12;

/** Podziały pola: dla pola (x, y) — czy należy do barwy drugiej. */
const DIVISIONS: { label: string; second: (x: number, y: number, w: number, h: number) => boolean }[] = [
  { label: 'jednolite', second: () => false },
  { label: 'w słup', second: (x, _y, w) => x >= w / 2 },
  { label: 'w pas', second: (_x, y, _w, h) => y >= h / 2 },
  { label: 'w skos', second: (x, y, w, h) => x / w > y / h },
  { label: 'w krzyż', second: (x, y, w, h) => Math.abs(x + 0.5 - w / 3) < w / 12 || Math.abs(y + 0.5 - h / 2) < h / 10 },
  { label: 'w czwórdzielne', second: (x, y, w, h) => x < w / 2 !== y < h / 2 },
];

export function openBannerEditor(
  container: HTMLElement,
  spec: BannerSpec,
  token: string
): Promise<Banner | null> {
  return new Promise((resolve) => {
    const design: BannerDesign = {
      shape: spec.shapes[0]?.key ?? 'rect',
      pixels: new Array(spec.width * spec.height).fill(0),
    };
    let primary = 2;
    let secondary = 1;
    let tool: Tool = 'pencil';
    let painting: number | null = null;

    const overlay = document.createElement('div');
    overlay.className = 'banner-editor';
    overlay.innerHTML = `
      <div class="banner-sheet">
        <h2>Twój sztandar</h2>
        <p class="banner-warning">Sztandar tworzy się <strong>raz</strong> — po zapisaniu nie da się go zmienić.</p>
        <div class="banner-row banner-shapes"></div>
        <div class="banner-body">
          <canvas class="banner-canvas"></canvas>
          <div class="banner-side">
            <div class="banner-label">barwy <span>lewy · prawy przycisk</span></div>
            <div class="banner-palette"></div>
            <div class="banner-label">narzędzie</div>
            <div class="banner-row banner-tools"></div>
            <div class="banner-label">podział pola</div>
            <div class="banner-row banner-divisions"></div>
          </div>
        </div>
        <label class="banner-house">
          <span class="banner-label">ród <span>opcjonalnie — też raz i na zawsze</span></span>
          <span class="banner-house-row">Własność rodu <input type="text" maxlength="24" placeholder="np. Wiśniowieckich" /></span>
        </label>
        <p class="banner-error" hidden></p>
        <div class="banner-actions">
          <button type="button" class="banner-save">wznieś sztandar</button>
          <button type="button" class="banner-cancel">anuluj</button>
        </div>
      </div>
    `;

    const canvas = overlay.querySelector('.banner-canvas') as HTMLCanvasElement;
    const flat = document.createElement('canvas');
    flat.width = spec.width;
    flat.height = spec.height;
    canvas.width = spec.width * CELL;
    canvas.height = spec.height * CELL;
    const context = canvas.getContext('2d')!;
    context.imageSmoothingEnabled = false;
    const error = overlay.querySelector('.banner-error') as HTMLElement;

    const mask = () => spec.shapes.find((shape) => shape.key === design.shape)?.mask ?? [];

    const draw = () => {
      paintBanner(flat, design, spec);
      context.clearRect(0, 0, canvas.width, canvas.height);
      // Pola spoza kształtu: szachownica, żeby było widać, że ich nie ma.
      const m = mask();
      for (let y = 0; y < spec.height; y++) {
        for (let x = 0; x < spec.width; x++) {
          if (m[y * spec.width + x] === 1) continue;
          context.fillStyle = (x + y) % 2 ? '#1a1e25' : '#222733';
          context.fillRect(x * CELL, y * CELL, CELL, CELL);
        }
      }
      context.drawImage(flat, 0, 0, canvas.width, canvas.height);
      // Siatka, cienko.
      context.strokeStyle = 'rgba(0,0,0,0.18)';
      context.lineWidth = 1;
      for (let x = 0; x <= spec.width; x++) {
        context.beginPath();
        context.moveTo(x * CELL + 0.5, 0);
        context.lineTo(x * CELL + 0.5, canvas.height);
        context.stroke();
      }
      for (let y = 0; y <= spec.height; y++) {
        context.beginPath();
        context.moveTo(0, y * CELL + 0.5);
        context.lineTo(canvas.width, y * CELL + 0.5);
        context.stroke();
      }
    };

    const cellAt = (event: MouseEvent): [number, number] | null => {
      const box = canvas.getBoundingClientRect();
      const x = Math.floor(((event.clientX - box.left) / box.width) * spec.width);
      const y = Math.floor(((event.clientY - box.top) / box.height) * spec.height);
      if (x < 0 || y < 0 || x >= spec.width || y >= spec.height) return null;
      return mask()[y * spec.width + x] === 1 ? [x, y] : null;
    };

    const fill = (x: number, y: number, colour: number) => {
      const m = mask();
      const target = design.pixels[y * spec.width + x];
      if (target === colour) return;
      const stack: [number, number][] = [[x, y]];
      while (stack.length > 0) {
        const [cx, cy] = stack.pop()!;
        if (cx < 0 || cy < 0 || cx >= spec.width || cy >= spec.height) continue;
        const i = cy * spec.width + cx;
        if (m[i] !== 1 || design.pixels[i] !== target) continue;
        design.pixels[i] = colour;
        stack.push([cx + 1, cy], [cx - 1, cy], [cx, cy + 1], [cx, cy - 1]);
      }
    };

    const apply = (event: MouseEvent) => {
      const cell = cellAt(event);
      if (!cell || painting === null) return;
      const [x, y] = cell;
      if (tool === 'fill') fill(x, y, painting);
      else design.pixels[y * spec.width + x] = painting;
      draw();
    };

    canvas.addEventListener('contextmenu', (event) => event.preventDefault());
    canvas.addEventListener('mousedown', (event) => {
      painting = event.button === 2 ? secondary : primary;
      apply(event);
      if (tool === 'fill') painting = null;
    });
    canvas.addEventListener('mousemove', (event) => {
      if (tool === 'pencil') apply(event);
    });
    const release = () => (painting = null);
    window.addEventListener('mouseup', release);

    // Kształty.
    const shapes = overlay.querySelector('.banner-shapes') as HTMLElement;
    const shapeButtons = spec.shapes.map((shape) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = shape.label;
      button.addEventListener('click', () => {
        design.shape = shape.key;
        shapeButtons.forEach((b) => b.classList.toggle('active', b === button));
        draw();
      });
      shapes.appendChild(button);
      return button;
    });
    shapeButtons[0]?.classList.add('active');

    // Paleta.
    const palette = overlay.querySelector('.banner-palette') as HTMLElement;
    const swatches = spec.palette.map((hex, index) => {
      const swatch = document.createElement('button');
      swatch.type = 'button';
      swatch.className = 'banner-swatch';
      swatch.style.background = hex;
      swatch.addEventListener('click', () => {
        primary = index;
        mark();
      });
      swatch.addEventListener('contextmenu', (event) => {
        event.preventDefault();
        secondary = index;
        mark();
      });
      palette.appendChild(swatch);
      return swatch;
    });
    const mark = () =>
      swatches.forEach((swatch, index) => {
        swatch.classList.toggle('primary', index === primary);
        swatch.classList.toggle('secondary', index === secondary);
      });
    mark();

    // Narzędzia.
    const tools = overlay.querySelector('.banner-tools') as HTMLElement;
    const toolButtons = (['pencil', 'fill'] as Tool[]).map((which) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = which === 'pencil' ? 'ołówek' : 'wypełnianie';
      button.addEventListener('click', () => {
        tool = which;
        toolButtons.forEach((b) => b.classList.toggle('active', b === button));
      });
      tools.appendChild(button);
      return button;
    });
    toolButtons[0].classList.add('active');

    // Podziały.
    const divisions = overlay.querySelector('.banner-divisions') as HTMLElement;
    for (const division of DIVISIONS) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = division.label;
      button.addEventListener('click', () => {
        for (let y = 0; y < spec.height; y++) {
          for (let x = 0; x < spec.width; x++) {
            design.pixels[y * spec.width + x] = division.second(x, y, spec.width, spec.height)
              ? secondary
              : primary;
          }
        }
        draw();
      });
      divisions.appendChild(button);
    }

    const close = (result: Banner | null) => {
      window.removeEventListener('mouseup', release);
      overlay.remove();
      resolve(result);
    };

    (overlay.querySelector('.banner-cancel') as HTMLButtonElement).addEventListener('click', () =>
      close(null)
    );
    const save = overlay.querySelector('.banner-save') as HTMLButtonElement;
    save.addEventListener('click', async () => {
      const house = (overlay.querySelector('.banner-house input') as HTMLInputElement).value.trim();
      const question = house
        ? `Wznieść ten sztandar rodu ${house}? Później nie da się zmienić ani sztandaru, ani rodu.`
        : 'Wznieść ten sztandar? Później nie da się go zmienić.';
      if (!window.confirm(question)) return;
      save.disabled = true;
      error.hidden = true;
      const answer = await createBanner(token, { ...design, house: house || undefined }).catch(
        () => ({ code: 'broken', message: 'serwer nie odpowiada' }) as const
      );
      if ('code' in answer) {
        error.textContent = answer.message;
        error.hidden = false;
        save.disabled = false;
        return;
      }
      close(answer);
    });

    container.appendChild(overlay);
    draw();
  });
}

/** Mały obrazek sztandaru — do kart i list. */
export function bannerThumb(design: BannerDesign, spec: BannerSpec, scale = 3): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = spec.width;
  canvas.height = spec.height;
  paintBanner(canvas, design, spec);
  canvas.className = 'banner-thumb';
  canvas.style.width = `${spec.width * scale}px`;
  canvas.style.height = `${spec.height * scale}px`;
  return canvas;
}
